import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  getDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
} from 'firebase/firestore';
import { db, auth } from './firebase';
import { sanitiseString } from '@/utils/validators';

const COLLECTION = 'planned_expenses';

function toPlanned(snap) {
  const d = snap.data();
  return {
    id:          snap.id,
    date:        d.date,
    vendor:      d.vendor,
    amount:      d.amount,
    description: d.description,
    label:       d.label,
    createdBy:   d.createdBy,
    createdAt:   d.createdAt?.toDate?.()?.toISOString() ?? null,
  };
}

/** Add a new planned expense */
export async function addPlannedExpense(expense) {
  const user = auth.currentUser;
  const ref = await addDoc(collection(db, COLLECTION), {
    date:        expense.date,
    vendor:      sanitiseString(expense.vendor),
    amount:      Number(expense.amount),
    description: sanitiseString(expense.description),
    label:       expense.label,
    createdBy:   user?.email ?? 'unknown',
    createdAt:   serverTimestamp(),
  });
  return ref.id;
}

/** Update a planned expense */
export async function updatePlannedExpense(id, expense) {
  await updateDoc(doc(db, COLLECTION, id), {
    date:        expense.date,
    vendor:      sanitiseString(expense.vendor),
    amount:      Number(expense.amount),
    description: sanitiseString(expense.description),
    label:       expense.label,
    updatedAt:   serverTimestamp(),
  });
}

/** Delete a planned expense */
export async function deletePlannedExpense(id) {
  await deleteDoc(doc(db, COLLECTION, id));
}

/** Get all planned expenses, optionally filtered by year and/or label */
export async function getPlannedExpenses(filters = {}) {
  let q = query(collection(db, COLLECTION), orderBy('date', 'desc'));

  if (filters.label) {
    q = query(q, where('label', '==', filters.label));
  }

  const snap = await getDocs(q);
  let records = snap.docs.map(toPlanned);

  if (filters.year) {
    records = records.filter(r => r.date.startsWith(filters.year));
  }

  return records;
}

/** Get yearly total for planned expenses */
export async function getPlannedYearTotal(year) {
  const records = await getPlannedExpenses({ year });
  return records.reduce((sum, r) => sum + r.amount, 0);
}

/** Get planned expenses for a specific month (for dashboard callout) */
export async function getPlannedForMonth(month) {
  const snap = await getDocs(
    query(
      collection(db, COLLECTION),
      where('date', '>=', `${month}-01`),
      where('date', '<=', `${month}-31`)
    )
  );
  return snap.docs.map(toPlanned);
}

/**
 * Migrate all planned expenses from one label to another.
 * Used when a label is deleted — moves records to 'big_purchase'.
 */
export async function bulkRelabelPlanned(fromLabel, toLabel) {
  const snap = await getDocs(
    query(collection(db, COLLECTION), where('label', '==', fromLabel))
  );
  const updates = snap.docs.map(d =>
    updateDoc(doc(db, COLLECTION, d.id), { label: toLabel, updatedAt: serverTimestamp() })
  );
  await Promise.all(updates);
  return snap.size;
}
